import React, { useEffect, useMemo } from "react";
import CetificateTabs from "./components/CertificateTabs";
import CertificateInfoTab from "./components/CertificateInfoTab";
import CertificateTestimonialTab from "./components/CertificateTestimonialTab";
import CertificateStatsTab from "./components/CertificateStatsTab";
import PathInfoTab from "./components/PathInfoTab";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { useInView } from "react-intersection-observer";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import getSortedComponentsBySectionOrder from "@/utils/getSortedComponentsBySectionOrder";
import { PathInfo } from "./components/PathInfoTab/types";
import {
  CertificateEmphasisInformation,
  CertificateEmphasisStats,
  CertificateEmphasisTestimonial,
} from "../types";
import { ProcessedCourse } from "@/common/course/types";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";
import { useCurrency } from "@/contexts/UserPreferencesContext/hooks/useCurrency";
import useTranslations from "@/i18n/useTranslations";

interface CertificateEmphasisTabsProps {
  information?: CertificateEmphasisInformation;
  stats?: CertificateEmphasisStats;
  testimonial?: CertificateEmphasisTestimonial;
  tabOrder?: string[];
  ctaElementRenderer: (
    tabId: string,
    ctaClassName: string,
  ) => React.JSX.Element;
  pathInfoCoursePrice?: number | null;
  hidePrice?: boolean;
  pathInfo?: PathInfo;
  course?: ProcessedCourse;
}

export default function CertificateEmphasisTabs({
  information,
  stats,
  testimonial,
  tabOrder: externalTabOrder,
  ctaElementRenderer,
  pathInfoCoursePrice,
  hidePrice,
  pathInfo,
  course,
}: CertificateEmphasisTabsProps) {
  const t = useTranslations("Course");
  const { currencySelector } = useSingleCourseTrafficConfig({ course: course });
  const { getConvertedProductPrice } = useCurrency();

  const { convertedPrice, displayCurrency } = useMemo(() => {
    if (!pathInfoCoursePrice || !course) {
      return { convertedPrice: null, displayCurrency: undefined };
    }
    return getConvertedProductPrice(pathInfoCoursePrice, currencySelector);
  }, [pathInfoCoursePrice, currencySelector, getConvertedProductPrice, course]);
  const componentList = [
    {
      key: "about",
      renderer: () => {
        return <CertificateInfoTab externalData={information} />;
      },
    },
    {
      key: "testimonials",
      renderer: () => {
        return <CertificateTestimonialTab externalData={testimonial} />;
      },
    },
    {
      key: "stats",
      renderer: () => {
        return <CertificateStatsTab externalData={stats} />;
      },
    },
  ];

  // PathInfo should only show if there is a course price provided
  if (pathInfoCoursePrice) {
    componentList.push({
      key: "paths",
      renderer: () => {
        return (
          <PathInfoTab
            coursePrice={pathInfoCoursePrice}
            convertedPrice={convertedPrice}
            displayCurrency={displayCurrency}
            hidePrice={hidePrice}
            externalData={pathInfo}
          />
        );
      },
    });
  }

  const orderedComponents = getSortedComponentsBySectionOrder({
    componentList,
    sectionsOrder: externalTabOrder,
    returnObjectList: true,
  });

  const tabHeadingMap: { [key: string]: string } = {
    about: t("aboutThisCertificate"),
    testimonials: t("whatOthersAreSaying"),
    stats: t("advanceYourCareer"),
    paths: t("waysToTakeCourse"),
  };

  const orderedTabs = orderedComponents.map((componentObject) => {
    return {
      tabHeading: tabHeadingMap[componentObject.key],
      id: componentObject.key,
      content: componentObject.renderer(),
    };
  });

  const webElement: ElementViewed["webElement"] = {
    elementType: ElementType.Component,
    htmlId: "value-prop",
    location: "cert-module",
    position: "1",
    name: "value-prop-module",
  };

  const { ref, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);
  const { elementViewed } = useElementEvents({ webElement });

  useEffect(() => {
    if (inView) {
      elementViewed();
    }
  }, [inView, elementViewed]);

  return (
    <div ref={ref}>
      <CetificateTabs
        tabs={orderedTabs}
        ctaElementRenderer={ctaElementRenderer}
      />
    </div>
  );
}
